import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AppService } from '@App';
import { NoteTypes } from '@Enums';
import { CaseAttachment, AgentCommunicationRequest } from '@Models';
import {
    CaseDocumentsDataService,
    FileUploadService,
    NotificationService,
    NotificationSeverity,
} from '@Services';
import { SharedModule } from '@Shared/shared.module';
import { CaseRequirementResponse, CreateMyNoteRequest } from '@Underwriting/models';
import { UWRequirementsDataService, UWWorkbenchService } from '@Underwriting/services';
import { FileUtils } from '@Utils';
import { SubSink } from 'subsink';
import { lastValueFrom } from 'rxjs';

import { AgentCommunicationDialogData, AgentCommunicationDialogResponse } from './models';
import { MrsBtnTertiaryComponent } from '../../../../../../shared/components/mrs-buttons/mrs-btn-tertiary/mrs-btn-tertiary.component';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CaseRequirementStatuses } from '@CaseSummary/underwriting/types';

@Component({
    selector: 'agent-communication-dialog',
    templateUrl: './agent-communication-dialog.component.html',
    styleUrls: ['./agent-communication-dialog.component.scss'],
    imports: [
        TextFieldModule,
        MrsBtnTertiaryComponent,
        SharedModule
    ]
})
export default class AgentCommunicationDialog implements OnInit, OnDestroy {
    @ViewChild('fileInput', { static: false }) fileInputRef: ElementRef;

    readonly Error_UnexpectedUpload = 'Something unexpected went wrong trying to upload the document.';
    readonly Error_NoDocuments = 'No documents selected.';
    readonly Error_DocumentExtension = 'Only pdf and Tiff files are allowed to be uploaded';
    readonly Error_UploadFailed = 'Upload failed.';
    readonly Msg_AttachmentUploaded = 'Attachment added successfully';

    dialogTitle = 'New Agent Communication';
    caseRequirement: CaseRequirementResponse;
    uploadFileList: File[] = [];
    note: string = '';

    sending = false;
    noteError = false;

    // Feature Toggle
    includeInCasePackageEnabled = false;  

    subs = new SubSink();

    constructor(
        public dialogRef: MatDialogRef<AgentCommunicationDialog, AgentCommunicationDialogResponse>,
        @Inject(MAT_DIALOG_DATA) public data: AgentCommunicationDialogData,
        private caseDocumentsDataService: CaseDocumentsDataService,
        private notificationService: NotificationService,
        private fileUploadService: FileUploadService,
        private uwWorkbenchService: UWWorkbenchService,
        private uwRequirementsDataService: UWRequirementsDataService,
        public appService: AppService,
    ) {
    }

    //#region Helpers

    calculateFileSize(fileSizeInBytes: number): string {
        if (fileSizeInBytes < 1024) {
            return `${fileSizeInBytes} bytes`;
        } else if (fileSizeInBytes < 1048576) {
            const sizeInKB = (fileSizeInBytes / 1024).toFixed(2);
            return `${sizeInKB} KB`;
        } else {
            const sizeInMB = (fileSizeInBytes / 1048576).toFixed(2);
            return `${sizeInMB} MB`;
        }  
    }

    formatCaseRequirementPurpose(caseRequirement: CaseRequirementResponse): string {
        if (caseRequirement.subtitle) {
            return `UW Requirement ${caseRequirement.name}: ${caseRequirement.subtitle}`;
        }
        return `UW Requirement ${caseRequirement.name}`;
    }

    getIncludeInCasePackageFlag(): boolean {
        if (!this.includeInCasePackageEnabled)
            return false;

        return this.data.requirement?.includeInCasePackage || false;
    }

    //#endregion
    //#region Subscriptions
    
    subscribeToCaseRequirements() {
        this.subs.add(this.uwRequirementsDataService.loadCaseRequirements(this.data.client.code, this.data.caseRequirement.caseId).subscribe({
            next: caseRequirementResponses => {
                const caseRequirements = caseRequirementResponses || [];
                this.caseRequirement = caseRequirements.find(x => x.id === this.data.caseRequirement.id);
            },
        }));
    }

    //#endregion
    //#region Lifecycle

    ngOnInit() {
        this.dialogTitle = this.data.title;

        this.subscribeToCaseRequirements();
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion
    //#region Handlers

    async handleAddFiles(event: Event) {
        const target = event.target as HTMLInputElement;

        if (!target?.files)
            return this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: this.Error_UnexpectedUpload, log: true });
        if (target.files.length <= 0)
            return this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: this.Error_NoDocuments, log: true });
        
        const files = Array.from(target.files);
        files.forEach(file => {
            if (FileUtils.isAllowedEvidenceExtensions(file.name)) {
                this.uploadFileList.push(file);
            } else {
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: this.Error_DocumentExtension, log: true });
            }
        });

        target.value = '';
    }

    handleDelete(file: File) {
        const index = this.uploadFileList.indexOf(file);
        if (index > -1) {
            this.uploadFileList.splice(index, 1);
        }
    }

    createMyNoteRequest(note: string, isInternalNote: boolean): CreateMyNoteRequest {
        return {
            caseId: this.caseRequirement.caseId,
            message: note,
            isInternalNote: isInternalNote,
            excludeFromMasterList: true,
            caseRequirement: {
                caseRequirementName: this.caseRequirement.name,
                caseRequirementSubtitle: this.caseRequirement.subtitle,
                caseRequirementId: this.caseRequirement.id
            },
            noteTypeId: NoteTypes.AgentCommunicationUI
        };
    }

    async handleSend() {
        if (!this.note?.trim().length) {
            this.noteError = true;
            return;
        }

        this.sending = true;
        this.appService.forceHideLoadingIndicator = true;

        try {
            if (this.data.caseRequirement.status ===  CaseRequirementStatuses.NotOrdered) {
                await lastValueFrom(this.uwWorkbenchService.updateCaseRequirementStatus(this.data.caseRequirement.id, CaseRequirementStatuses.Ordered, this.data.account.id));
            }

            const noteRequest = this.createMyNoteRequest(this.note, false);
            const noteResponse = await lastValueFrom(this.uwWorkbenchService.addMyNote(this.data.account.id, noteRequest));

            const documentIds: string[] = [];

            const caseAttachment: CaseAttachment = {
                includeInCasePackage: this.getIncludeInCasePackageFlag(),
                tags: ['UW Evidence', 'Outbound'],
                caseId: this.caseRequirement.caseId,
                attachmentPurpose: this.formatCaseRequirementPurpose(this.caseRequirement)
            };
            for (const file of this.uploadFileList) {
                const commitResult$ = await this.fileUploadService.sendCaseAttachmentInChunks(this.data.client.id, this.data.account.id, this.caseRequirement.caseId, caseAttachment, file);

                const caseDocumentId = await lastValueFrom(commitResult$);
                documentIds.push(caseDocumentId);

                await lastValueFrom(this.uwWorkbenchService.updateCaseRequirementAttachment(this.caseRequirement.id, caseDocumentId, this.data.account.id));
            }
            this.caseDocumentsDataService.reloadAttachments(this.caseRequirement.caseId);

            const caseAgentCommunicationRequest: AgentCommunicationRequest = {
                caseRequirementId: this.caseRequirement.id,
                noteId: noteResponse?.id,
                documentIds: documentIds
            };
            await lastValueFrom(this.uwWorkbenchService.raiseAgentCommunication(this.data.client.code, this.data.caseRequirement.caseId, caseAgentCommunicationRequest));

            this.notificationService.showNotification({ severity: NotificationSeverity.Success, message: "Added note successfully..."});
            this.dialogRef.close({ action: 'send' });
        } catch (err) {
            this.notificationService.showNotification({severity: NotificationSeverity.Error, message: err, log: true});
        } finally {
            this.sending = false;
            this.appService.forceHideLoadingIndicator = false;
        }
    }

    handleCancel() {
        this.dialogRef.close({ action: 'cancel' });
    }

    //#endregion
}
